import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingBag, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import ContactButton from '../components/ContactButton';

const Collection = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollection();
  }, [handle]);

  const fetchCollection = async () => {
    try {
      setLoading(true);
      
      // Fetch collection with products from Shopify
      const response = await api.get(`/shopify/collections/${handle}`);
      
      if (response.data.success && response.data.collection) {
        const collectionData = response.data.collection;
        setCollection(collectionData);
        setProducts(collectionData.products || []);
      } else {
        toast.error('Collection not found');
      }
      
    } catch (error) {
      console.error('Failed to load collection:', error);
      toast.error('Failed to load collection. Please check if this collection exists in Shopify.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-charcoal border-t-warmSage mb-4"></div>
          <p className="text-warmGray">Loading collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-cream hover:text-warmSage">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={() => navigate('/shop')} className="bg-warmSage text-charcoal hover:bg-warmSage/90">
                Shop All
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-6 pt-32 pb-16">
        {/* Collection Header */}
        <div className="text-center mb-12">
          <Badge className="bg-charcoal text-cream mb-4 uppercase">{collection?.title || 'Collection'}</Badge>
          <h1 className="text-6xl font-black text-charcoal uppercase mb-4">
            {collection?.title || 'Collection'}
          </h1>
          <p className="text-xl text-warmGray max-w-2xl mx-auto">
            {collection?.description || 'Discover our curated collection'}
          </p>
        </div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {products.map((product) => (
              <Card
                key={product.id}
                className="product-card-streetwear group cursor-pointer"
                onClick={() => navigate(`/products/${product.handle}`)}
              >
                <div className="relative overflow-hidden aspect-[4/5] bg-warmGray/10">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-warmGray/20 flex items-center justify-center">
                      <ShoppingBag className="h-20 w-20 text-warmGray" />
                    </div>
                  )}
                  <div className="product-overlay-streetwear">
                    <Button className="btn-primary-streetwear">VIEW PRODUCT</Button>
                  </div>
                  {product.category && (
                    <div className="product-badge-overlay">
                      <Badge className="badge-light">{product.category}</Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-6 bg-charcoal">
                  <h3 className="product-title-streetwear text-lg mb-2">{product.name}</h3>
                  <p className="product-desc-streetwear text-sm mb-3 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <p className="product-price-streetwear text-2xl">${product.price.toFixed(2)}</p>
                    {product.tags && product.tags.includes('made-to-order') ? (
                      <Badge 
                        className="bg-blue-600 text-white font-bold cursor-pointer hover:bg-blue-700 transition-all whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/pricing-policy');
                        }}
                        title="Click to learn about Made to Order items"
                      >
                        MADE TO ORDER ℹ️
                      </Badge>
                    ) : (
                      <Badge className={product.available ? 'bg-green-500' : 'bg-red-500'}>
                        {product.available ? 'In Stock' : 'Sold Out'}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <ShoppingBag className="h-20 w-20 text-warmGray mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-charcoal mb-2">No products in this collection yet</h3>
            <p className="text-warmGray mb-6">Check back soon for new arrivals</p>
            <Button onClick={() => navigate('/shop')}>
              Browse All Products
            </Button>
          </div>
        )}
      </div>

      <ContactButton />
    </div>
  );
};

export default Collection;
